/**
 * CR-7304 — the NCR register opened from a PO header's "NCRs raised against
 * this PO" card (`/quality?po=<id>&po_number=<number>`).
 *
 * `po` is the filter (backend `NCRFilterSet.filter_po`, the same rule that
 * counted the NCRs on the header); `po_number` is only the label, so the user
 * can see why the list is narrowed and clear it.
 */
import { useSearchParams } from 'react-router-dom';
import { X, FileText } from 'lucide-react';

export interface PODeepLink {
  /** The purchase order id to narrow by, or '' when the page was not opened from a PO. */
  poId: string;
  poNumber: string;
  /** Drops the PO narrowing from the URL (and so from every request). */
  clear: () => void;
}

/**
 * Reads the PO deep-link from the URL.
 *
 * @returns The PO id / number the register is narrowed to, and a way to clear it.
 */
export function usePODeepLink(): PODeepLink {
  const [searchParams, setSearchParams] = useSearchParams();
  const poId = searchParams.get('po') ?? '';
  const poNumber = searchParams.get('po_number') ?? '';
  const clear = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('po');
    next.delete('po_number');
    setSearchParams(next, { replace: true });
  };
  return { poId, poNumber, clear };
}

interface NCRPODeepLinkChipProps {
  deepLink: PODeepLink;
  onClear: () => void;
}

/** "Purchase order PRO… ×" above the list while it is narrowed to one order. */
export default function NCRPODeepLinkChip({ deepLink, onClear }: NCRPODeepLinkChipProps) {
  if (!deepLink.poId) return null;
  return (
    <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/5 px-3 py-1 text-sm">
      <FileText className="h-3.5 w-3.5 text-primary" />
      <span>
        NCRs raised against purchase order <span className="font-medium">{deepLink.poNumber || `#${deepLink.poId}`}</span>
      </span>
      <button
        type="button"
        onClick={onClear}
        className="rounded-full p-0.5 hover:bg-primary/10"
        aria-label="Clear purchase order filter"
        title="Show all NCRs"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
